// Bloc « Documents » de la fiche vehicule : carte grise, assurance, contrat,
// rapports archives.
//
// Le telechargement passe par `telecharger()` et non par un <a href> : le
// fichier est servi par une route AUTHENTIFIEE, et un lien direct ne porte
// pas le jeton -- il repondrait 401 sans rien dire.
import { useCallback, useEffect, useState } from "react";

import api, { telecharger } from "../api";
import { formaterDate } from "../format";
import ModaleConfirmation from "./ModaleConfirmation";

// Les libelles vivent ici, les valeurs cote serveur (schemas.TypeDocument).
// Un type ajoute au serveur sans l'etre ici s'affichera par sa valeur brute
// plutot que de casser l'ecran.
const TYPES = [
  { valeur: "carte_grise", label: "Carte grise" },
  { valeur: "assurance", label: "Assurance" },
  { valeur: "financement", label: "Financement" },
  { valeur: "rapport_vgp", label: "Rapport VGP" },
  { valeur: "rapport_ct", label: "Rapport CT" },
  { valeur: "autre", label: "Autre" },
];

// Types PRODUITS PAR L'APPLICATION : ils s'affichent, mais ne se proposent PAS au
// dépôt. Un contrat de mise à disposition (location) signé vient du module Location, qui le fige à
// la signature ; le laisser déposer à la main ferait passer n'importe quel PDF
// pour un contrat signé dans l'application.
const TYPES_PRODUITS = [
  { valeur: "contrat_location", label: "Contrat de mise à disposition" },
  { valeur: "retour_location", label: "Restitution de location" },
  { valeur: "avenant_location", label: "Avenant de location" },
];

const libelleType = (valeur) =>
  [...TYPES, ...TYPES_PRODUITS].find((t) => t.valeur === valeur)?.label || valeur;

function formaterTaille(octets) {
  if (!octets) return "—";
  // « 0 Ko » se lit comme un fichier vide : on plancher a « < 1 Ko ».
  if (octets < 1024) return "< 1 Ko";
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function DocumentsVehicule({ vehiculeId }) {
  const [documents, setDocuments] = useState(null); // null = pas encore charge
  const [erreur, setErreur] = useState("");
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [type, setType] = useState("carte_grise");
  const [libelle, setLibelle] = useState("");

  const charger = useCallback(() => {
    api
      .get(`/vehicules/${vehiculeId}/documents`)
      .then(({ data }) => setDocuments(data))
      .catch(() => setErreur("Impossible de charger les documents."));
  }, [vehiculeId]);

  useEffect(charger, [charger]);

  function envoyer(evenement) {
    const fichier = evenement.target.files?.[0];
    // On vide le champ tout de suite : sans cela, redeposer le MEME fichier
    // apres une erreur ne declenche aucun evenement, et l'utilisateur croit
    // que le bouton ne repond plus.
    evenement.target.value = "";
    if (!fichier) return;

    setEnvoiEnCours(true);
    setErreur("");
    const corps = new FormData();
    corps.append("fichier", fichier);
    corps.append("type_document", type);
    if (libelle.trim()) corps.append("libelle", libelle.trim());

    api
      .post(`/vehicules/${vehiculeId}/documents`, corps)
      .then(() => {
        setLibelle("");
        charger();
      })
      .catch((e) => {
        // Le message du serveur est plus utile qu'un texte generique : il dit
        // POURQUOI (format refuse, fichier trop lourd).
        setErreur(
          e?.response?.data?.detail || "Le document n'a pas pu être ajouté."
        );
      })
      .finally(() => setEnvoiEnCours(false));
  }

  function supprimer(doc) {
    setConfirmation({
      titre: "Supprimer ce document ?",
      sousTitre: doc.libelle || libelleType(doc.type_document),
      message: "Le fichier est effacé, et cette action est irréversible.",
      libelleConfirmer: "Supprimer",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/documents/${doc.id}`);
        charger();
      },
    });
  }

  return (
    <section className="carte">
      {confirmation && (
        <ModaleConfirmation {...confirmation} onFermer={() => setConfirmation(null)} />
      )}
      <div className="entete-section">
        <h3>Documents</h3>
        <label className="btn-accent">
          {envoiEnCours ? "Envoi…" : "+ Ajouter un document"}
          <input
            type="file"
            // On annonce ce qu'on accepte : le selecteur de fichiers du
            // systeme filtre alors de lui-meme. Le serveur revalide aux
            // octets -- cet attribut est un confort, pas un controle.
            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
            onChange={envoyer}
            disabled={envoiEnCours}
            style={{ display: "none" }}
          />
        </label>
      </div>

      <div className="grille-form">
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((t) => (
              <option key={t.valeur} value={t.valeur}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Libellé (facultatif)
          <input
            type="text"
            value={libelle}
            placeholder="ex. Carte grise 2026"
            onChange={(e) => setLibelle(e.target.value)}
          />
        </label>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      {documents === null ? (
        <p className="vide">Chargement…</p>
      ) : documents.length === 0 ? (
        <p className="vide">
          Aucun document. Carte grise, assurance, contrat : PDF ou photo.
        </p>
      ) : (
        <table className="tableau">
          <thead>
            <tr>
              <th>Type</th>
              <th>Libellé</th>
              <th>Ajouté le</th>
              <th>Taille</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id}>
                <td>
                  <strong>{libelleType(doc.type_document)}</strong>
                </td>
                <td>{doc.libelle || doc.nom_origine || "—"}</td>
                <td>{formaterDate(doc.date_ajout)}</td>
                <td>{formaterTaille(doc.taille_octets)}</td>
                <td className="actions-cellule">
                  <button
                    className="btn"
                    onClick={() =>
                      telecharger(doc.url, doc.nom_origine || "document")
                    }
                  >
                    Ouvrir
                  </button>
                  <button className="lien-action lien-danger" onClick={() => supprimer(doc)}>
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
